import { useCallback } from "react";

import { SaleTypeForTerm, type InputMaybe } from "@/lib/gql/__generated__/graphql";

import type { ForNodeType } from "@/components/features/_model/types/auto-generator-for-view-model";

import type { DateRange } from "@/components/ui/calendar/types/calendar-range";
import type { FormOptionType } from "@/components/ui/form/_types/type";

type Props = {
	saleTypeForTerm: ForNodeType<SaleTypeForTerm>;
	saleStart: ForNodeType<InputMaybe<string> | undefined> | undefined;
	saleEnd: ForNodeType<InputMaybe<string> | undefined> | undefined;
	saleStartTime: ForNodeType<InputMaybe<string> | undefined> | undefined;
	saleEndTime: ForNodeType<InputMaybe<string> | undefined> | undefined;
	stayableStart: ForNodeType<InputMaybe<string> | undefined> | undefined;
	stayableEnd: ForNodeType<InputMaybe<string> | undefined> | undefined;

	onSetViewModel: (
		values: {
			key:
				| "saleEnd"
				| "saleStart"
				| "saleStartTime"
				| "saleEndTime"
				| "saleTypeForTerm"
				| "stayableEnd"
				| "stayableStart";
			value: ForNodeType<SaleTypeForTerm> | ForNodeType<InputMaybe<string> | undefined>;
		}[],
	) => void;
};

export const useSalesTermWithTime = ({
	saleTypeForTerm,
	saleStart,
	saleEnd,
	saleStartTime,
	saleEndTime,
	stayableStart,
	stayableEnd,
	onSetViewModel,
}: Props) => {
	const isEdited = saleTypeForTerm.isEdited;
	const hasError = Boolean(
		saleTypeForTerm.error ||
			saleStart?.error ||
			saleEnd?.error ||
			saleStartTime?.error ||
			saleEndTime?.error ||
			stayableStart?.error ||
			stayableEnd?.error,
	);

	const isLimitedSale = saleTypeForTerm.value === SaleTypeForTerm.Limited;

	const saleTypeForTermOptions = [
		{ id: `saleTypeForTermOptions_0`, label: "常に販売", value: SaleTypeForTerm.Always },
		{ id: `saleTypeForTermOptions_1`, label: "期間を限定して販売", value: SaleTypeForTerm.Limited },
	] satisfies FormOptionType[];

	const onSetSaleTypeForTerm = useCallback(
		(input: string) => {
			const inputToSaleTypeForTerm = input as SaleTypeForTerm;
			if (SaleTypeForTerm.Always === inputToSaleTypeForTerm) {
				onSetViewModel([
					{
						key: "saleTypeForTerm",
						value: {
							isEdited: true,
							value: SaleTypeForTerm.Always,
						},
					},
					{
						key: "saleStart",
						value: {
							isEdited: true,
							value: null,
						},
					},
					{
						key: "saleEnd",
						value: {
							isEdited: true,
							value: null,
						},
					},
					{
						key: "saleStartTime",
						value: {
							isEdited: true,
							value: null,
						},
					},
					{
						key: "saleEndTime",
						value: {
							isEdited: true,
							value: null,
						},
					},
					{
						key: "stayableStart",
						value: {
							isEdited: true,
							value: null,
						},
					},
					{
						key: "stayableEnd",
						value: {
							isEdited: true,
							value: null,
						},
					},
				]);
			} else {
				onSetViewModel([
					{
						key: "saleTypeForTerm",
						value: {
							isEdited: true,
							value: inputToSaleTypeForTerm,
						},
					},
					{
						key: "saleStart",
						value: {
							isEdited: true,
							value: "",
						},
					},
					{
						key: "saleEnd",
						value: {
							isEdited: true,
							value: "",
						},
					},
					{
						key: "saleStartTime",
						value: {
							isEdited: true,
							value: "",
						},
					},
					{
						key: "saleEndTime",
						value: {
							isEdited: true,
							value: "",
						},
					},
					{
						key: "stayableStart",
						value: {
							isEdited: true,
							value: "",
						},
					},
					{
						key: "stayableEnd",
						value: {
							isEdited: true,
							value: "",
						},
					},
				]);
			}
		},
		[onSetViewModel],
	);

	const onSetSaleTermWithTime = useCallback(
		(input: { saleStart?: string; saleEnd?: string; saleStartTime?: string; saleEndTime?: string }) => {
			const updates = [];

			if (input.saleStart !== undefined) {
				updates.push({
					key: "saleStart" as const,
					value: {
						isEdited: true,
						value: input.saleStart,
					},
				});
			}

			if (input.saleEnd !== undefined) {
				updates.push({
					key: "saleEnd" as const,
					value: {
						isEdited: true,
						value: input.saleEnd,
					},
				});
			}

			if (input.saleStartTime !== undefined) {
				updates.push({
					key: "saleStartTime" as const,
					value: {
						isEdited: true,
						value: input.saleStartTime,
					},
				});
			}

			if (input.saleEndTime !== undefined) {
				updates.push({
					key: "saleEndTime" as const,
					value: {
						isEdited: true,
						value: input.saleEndTime,
					},
				});
			}

			if (updates.length > 0) {
				onSetViewModel(updates);
			}
		},
		[onSetViewModel],
	);

	const onSetStayableTerm = useCallback(
		(input: DateRange) => {
			onSetViewModel([
				{
					key: "stayableStart",
					value: {
						isEdited: true,
						value: input.from,
					},
				},
				{
					key: "stayableEnd",
					value: {
						isEdited: true,
						value: input.to,
					},
				},
			]);
		},
		[onSetViewModel],
	);

	return {
		isEdited,
		hasError,

		isLimitedSale,

		saleTypeForTermOptions,
		saleTypeForTerm,
		onSetSaleTypeForTerm,

		saleStart,
		saleEnd,
		saleStartTime,
		saleEndTime,
		onSetSaleTermWithTime,

		stayableStart,
		stayableEnd,
		onSetStayableTerm,
	};
};
